import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Share2 } from 'lucide-react'
import ChatPanel from '../workspace/ChatPanel'
import CodeEditor from '../workspace/CodeEditor'
import type { Job } from '../../types'
import * as db from '../../services/db'

export default function ShareView() {
  const { token } = useParams<{ token: string }>()
  const [job, setJob] = useState<Job | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!token) { setNotFound(true); setLoading(false); return }

    db.fetchJobByToken(token)
      .then(async (fetched) => {
        if (!fetched) { setNotFound(true); return }
        setJob(fetched)
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [token])

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <p className="font-mono text-sm text-slate-500 animate-pulse">Loading shared conversation...</p>
      </div>
    )
  }

  if (notFound || !job) {
    return (
      <div className="min-h-screen bg-[#030712] flex flex-col items-center justify-center gap-4">
        <p className="font-mono text-sm text-slate-500">Shared conversation not found</p>
        <Link
          to="/"
          className="font-mono text-xs text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
        >
          Start your own project
        </Link>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#030712] text-slate-200">
      {/* Banner */}
      <div className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center gap-3 px-6 bg-[#030712]/80 backdrop-blur-md border-b border-cyan-500/20">
        <Share2 className="w-4 h-4 text-cyan-400" />
        <span className="font-mono text-xs font-semibold tracking-widest uppercase text-cyan-400">
          Shared Conversation
        </span>
        <span className="font-mono text-xs text-slate-500 truncate flex-1">
          {job.label}
        </span>
        <Link
          to="/"
          className="px-3 py-1.5 rounded-full font-mono text-xs border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-400/50 transition-all"
        >
          Start Your Own
        </Link>
      </div>

      {/* Content */}
      <div
        className="flex gap-3 p-3 overflow-hidden"
        style={{ height: 'calc(100vh - 3.5rem)', marginTop: '3.5rem' }}
      >
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="h-full w-1/3 shrink-0"
        >
          <ChatPanel
            messages={job.messages}
            isTyping={false}
            sendMessage={() => {}}
            readOnly
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
          className="flex-1 min-w-0 h-full"
        >
          <CodeEditor
            operations={job.operations}
            activeOperation={job.operations.find(op => op.id === job.activeOperationId) ?? job.operations[0]}
            onSelectOperation={() => {}}
            onAddOperation={() => {}}
            onRemoveOperation={() => {}}
            readOnly
          />
        </motion.div>
      </div>
    </div>
  )
}
